import React, { useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PhotoLightbox = ({ photo, onClose, onNext, onPrevious, hasNext, hasPrevious }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') onClose();
      if (e?.key === 'ArrowRight' && hasNext) onNext();
      if (e?.key === 'ArrowLeft' && hasPrevious) onPrevious();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, onNext, onPrevious, hasNext, hasPrevious]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: photo?.title,
          text: photo?.description,
          url: window.location?.href
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      navigator.clipboard?.writeText(window.location?.href);
      alert('Link copied to clipboard!');
    }
  };

  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(`Check out this amazing photo from Chapati Plus: ${photo?.title}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
        aria-label="Close lightbox"
      >
        <Icon name="X" size={24} color="white" />
      </button>
      {hasPrevious && (
        <button
          onClick={onPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          aria-label="Previous photo"
        >
          <Icon name="ChevronLeft" size={24} color="white" />
        </button>
      )}
      {hasNext && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          aria-label="Next photo"
        >
          <Icon name="ChevronRight" size={24} color="white" />
        </button>
      )}
      <div className="w-full h-full flex flex-col items-center justify-center p-4 md:p-8">
        <div className="max-w-6xl w-full">
          <div className="relative mb-4 md:mb-6">
            <Image
              src={photo?.image}
              alt={photo?.imageAlt}
              className="w-full max-h-[60vh] md:max-h-[70vh] object-contain rounded-lg"
            />
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:p-6">
            <div className="flex items-start justify-between gap-4 mb-3 md:mb-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-white text-lg md:text-2xl font-bold mb-2">{photo?.title}</h2>
                <p className="text-white/80 text-sm md:text-base">{photo?.description}</p>
              </div>
              <div className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs md:text-sm font-medium whitespace-nowrap">
                {photo?.categoryLabel}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              <div className="flex items-center gap-2 text-white/80 text-sm md:text-base">
                <Icon name="Heart" size={18} />
                <span>{photo?.likes} likes</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-sm md:text-base">
                <Icon name="Calendar" size={18} />
                <span>{photo?.date}</span>
              </div>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Share2"
                  iconPosition="left"
                  onClick={handleShare}
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                >
                  Share
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="MessageCircle"
                  iconPosition="left"
                  onClick={handleWhatsAppShare}
                  className="bg-accent hover:bg-accent/90"
                >
                  WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoLightbox;